import { type TargetAndTransition, type Transition } from 'motion/react';

/**
 * The superlike's send animation, as the approved board 1.1 plays it
 * (`_matchmaker/design/boards/Main.dc.html`, renderVals()): every phase,
 * time, transform and easing below is the board's. One phase follows
 * another on a timer (useSuperlikeSendAnimation); each element's look in a
 * phase is a pure function of the phase (superlikeFxFrame,
 * superlikeCardMotionTarget), so the sequence can be tested without a
 * browser.
 *
 * - launch0 (0 ms): the big heart sits on the button, at the button's size.
 * - launch (40 ms): it flies to the card's centre in 460 ms with an
 *   overshoot; the pink wash fades in over 420 ms.
 * - burst0 (480 ms): rings and particles appear at the centre.
 * - burst (520 ms): two rings grow and fade (the second 140 ms later), 12
 *   small hearts burst out, «Суперлайк» fades up under the heart.
 * - fly (1420 ms): the card flies up in 430 ms; the next card settles from
 *   scale .96 to 1.
 * - done (1880 ms).
 *
 * Under prefers-reduced-motion nothing flies: the wash, the heart and the
 * label fade in (fadeIn), then the card fades out (fadeOut).
 */
export type SuperlikeAnimationPhase =
  | 'idle'
  | 'launch0'
  | 'launch'
  | 'burst0'
  | 'burst'
  | 'fly'
  | 'fadeIn'
  | 'fadeOut';

export type SuperlikeAnimationStep = {
  phase: Exclude<SuperlikeAnimationPhase, 'idle'> | 'done';
  atMs: number;
};

/** When each phase starts, in ms from the send (board 1.1). */
export const SUPERLIKE_ANIMATION_MS = {
  launch: 40,
  burst0: 480,
  burst: 520,
  fly: 1420,
  done: 1880,
} as const;

/** The reduced-motion sequence: fade in, hold, fade the card out. */
export const SUPERLIKE_REDUCED_ANIMATION_MS = {
  fadeOut: 900,
  done: 1200,
} as const;

export type SuperlikeScheduleOptions = {
  isReducedMotion: boolean;
  /** A match: the sequence ends after the burst, without the card's flight. */
  isEndingAfterBurst: boolean;
};

export const superlikeAnimationSchedule = ({
  isReducedMotion,
  isEndingAfterBurst,
}: SuperlikeScheduleOptions): SuperlikeAnimationStep[] => {
  if (isReducedMotion) {
    return isEndingAfterBurst
      ? [
          { phase: 'fadeIn', atMs: 0 },
          { phase: 'done', atMs: SUPERLIKE_REDUCED_ANIMATION_MS.fadeOut },
        ]
      : [
          { phase: 'fadeIn', atMs: 0 },
          { phase: 'fadeOut', atMs: SUPERLIKE_REDUCED_ANIMATION_MS.fadeOut },
          { phase: 'done', atMs: SUPERLIKE_REDUCED_ANIMATION_MS.done },
        ];
  }

  const launch: SuperlikeAnimationStep[] = [
    { phase: 'launch0', atMs: 0 },
    { phase: 'launch', atMs: SUPERLIKE_ANIMATION_MS.launch },
    { phase: 'burst0', atMs: SUPERLIKE_ANIMATION_MS.burst0 },
    { phase: 'burst', atMs: SUPERLIKE_ANIMATION_MS.burst },
  ];

  return isEndingAfterBurst
    ? [...launch, { phase: 'done', atMs: SUPERLIKE_ANIMATION_MS.fly }]
    : [
        ...launch,
        { phase: 'fly', atMs: SUPERLIKE_ANIMATION_MS.fly },
        { phase: 'done', atMs: SUPERLIKE_ANIMATION_MS.done },
      ];
};

/** The big heart's size, and the button's: the heart starts at 36/216. */
export const SUPERLIKE_HEART_SIZE = 216;
const BUTTON_ICON_SIZE = 36;
/** The heart button: 56 px, 16 px from the photo's right and bottom edges. */
export const SUPERLIKE_BUTTON_SIZE = 56;
export const SUPERLIKE_BUTTON_INSET = 16;
/** The feed's button row under the photo (board A.3). */
export const FEED_ACTIONS_HEIGHT = 84;
const FEED_ACTIONS_ICON_SIZE = 54;
/** The heart's centre: (195, 340) on the board's 390 × 770 card. */
const CENTRE_Y_SHARE = 340 / 770;
/** «Суперлайк» sits 8 px under the heart: top 456 on the board. */
const LABEL_BELOW_CENTRE = 456 - 340;

const LAUNCH_EASE = [0.3, 1.35, 0.5, 1] as const;
const RING_EASE = [0.1, 0.6, 0.3, 1] as const;
const PARTICLE_EASE = [0.12, 0.7, 0.3, 1] as const;
const CARD_FLY_EASE = [0.55, 0, 0.8, 0.2] as const;
const FADE_S = 0.3;

const INSTANT: Transition = { duration: 0 };

/** The 12 small hearts: offset x, offset y, size, colour, rotation (board). */
export const SUPERLIKE_PARTICLES = [
  [-100, -150, 22, 'var(--graphic-light)', -30],
  [80, -170, 18, 'var(--brand-pink)', 25],
  [150, -60, 26, 'var(--premium-primary)', 40],
  [-160, -40, 20, '#ffd1e8', -45],
  [120, 90, 16, 'var(--graphic-light)', 20],
  [-120, 110, 18, 'var(--premium-primary)', -20],
  [20, -215, 24, '#ffd1e8', 10],
  [-40, 170, 14, 'var(--brand-pink)', -35],
  [190, 30, 14, 'var(--graphic-light)', 30],
  [-200, 60, 16, 'var(--brand-pink)', -15],
  [60, 175, 20, 'var(--graphic-light)', 35],
  [-70, -205, 14, 'var(--premium-primary)', -25],
] as const;

export type SuperlikeCardSize = { width: number; height: number };

/**
 * Where the heart starts: the corner of the photo, or the middle of the
 * feed's button row (the size is then the card's and the row's together).
 */
export type SuperlikeLaunchOrigin = 'photoCorner' | 'feedActions';

export type SuperlikeFxGeometry = {
  centreX: number;
  centreY: number;
  /** From the heart's centre to the button's centre. */
  launchX: number;
  launchY: number;
  /** The heart's scale on the button. */
  launchScale: number;
  labelTop: number;
};

export const superlikeFxGeometry = (
  { width, height }: SuperlikeCardSize,
  launchFrom: SuperlikeLaunchOrigin = 'photoCorner',
): SuperlikeFxGeometry => {
  const centreX = width / 2;

  if (launchFrom === 'feedActions') {
    const centreY = (height - FEED_ACTIONS_HEIGHT) * CENTRE_Y_SHARE;

    return {
      centreX,
      centreY,
      launchX: 0,
      launchY: height - FEED_ACTIONS_HEIGHT / 2 - centreY,
      launchScale: FEED_ACTIONS_ICON_SIZE / SUPERLIKE_HEART_SIZE,
      labelTop: centreY + LABEL_BELOW_CENTRE,
    };
  }

  const centreY = height * CENTRE_Y_SHARE;
  const buttonCentreOffset = SUPERLIKE_BUTTON_INSET + SUPERLIKE_BUTTON_SIZE / 2;

  return {
    centreX,
    centreY,
    launchX: width - buttonCentreOffset - centreX,
    launchY: height - buttonCentreOffset - centreY,
    launchScale: BUTTON_ICON_SIZE / SUPERLIKE_HEART_SIZE,
    labelTop: centreY + LABEL_BELOW_CENTRE,
  };
};

export type SuperlikeFxElementFrame = {
  animate: TargetAndTransition;
  transition: Transition;
};

export type SuperlikeParticleFrame = SuperlikeFxElementFrame & {
  size: number;
  color: string;
};

export type SuperlikeFxFrame = {
  heart: SuperlikeFxElementFrame;
  rings: SuperlikeFxElementFrame[];
  particles: SuperlikeParticleFrame[];
  label: SuperlikeFxElementFrame;
};

const isBursting = (phase: SuperlikeAnimationPhase) =>
  phase === 'burst' || phase === 'fly';

const heartFrame = (
  phase: SuperlikeAnimationPhase,
  { launchX, launchY, launchScale }: SuperlikeFxGeometry,
): SuperlikeFxElementFrame => {
  if (phase === 'launch0') {
    return {
      animate: {
        x: launchX,
        y: launchY,
        scale: launchScale,
        opacity: 1,
      },
      transition: INSTANT,
    };
  }

  if (phase === 'fadeIn' || phase === 'fadeOut') {
    return {
      animate: { x: 0, y: 0, scale: 1, opacity: 1 },
      transition: { opacity: { duration: FADE_S, ease: 'easeOut' } },
    };
  }

  return {
    animate: { x: 0, y: 0, scale: 1, opacity: 1 },
    transition: { duration: 0.46, ease: LAUNCH_EASE },
  };
};

/**
 * The pink wash over the photo (under the card's text, as on the board):
 * the existing like/dislike wash in pink, with a glow behind the heart.
 */
export const superlikeWashFrame = (
  phase: SuperlikeAnimationPhase,
): SuperlikeFxElementFrame => {
  if (phase === 'launch0') {
    return { animate: { opacity: 0 }, transition: INSTANT };
  }

  if (phase === 'fadeIn' || phase === 'fadeOut') {
    return {
      animate: { opacity: 1 },
      transition: { duration: FADE_S, ease: 'easeOut' },
    };
  }

  return {
    animate: { opacity: 1 },
    transition: { duration: 0.42, ease: 'easeOut' },
  };
};

const ringFrame = (
  phase: SuperlikeAnimationPhase,
  index: number,
): SuperlikeFxElementFrame => {
  if (isBursting(phase)) {
    const delay = index * 0.14;

    return {
      animate: { scale: 2.3, opacity: 0 },
      transition: {
        scale: { duration: 0.8, ease: RING_EASE, delay },
        opacity: { duration: 0.8, ease: 'easeOut', delay },
      },
    };
  }

  return {
    animate: { scale: 0.45, opacity: phase === 'burst0' ? 0.9 : 0 },
    transition: INSTANT,
  };
};

const particleFrames = (
  phase: SuperlikeAnimationPhase,
): SuperlikeParticleFrame[] =>
  SUPERLIKE_PARTICLES.map(([offsetX, offsetY, size, color, rotate]) =>
    isBursting(phase)
      ? {
          size,
          color,
          animate: {
            x: offsetX,
            y: offsetY - 40,
            scale: 1,
            rotate,
            opacity: 0,
          },
          transition: {
            duration: 0.95,
            ease: PARTICLE_EASE,
            opacity: { duration: 0.52, ease: 'easeIn', delay: 0.43 },
          },
        }
      : {
          size,
          color,
          animate: {
            x: 0,
            y: 0,
            scale: 0.3,
            rotate: 0,
            opacity: phase === 'burst0' ? 1 : 0,
          },
          transition: INSTANT,
        },
  );

const labelFrame = (
  phase: SuperlikeAnimationPhase,
): SuperlikeFxElementFrame => {
  if (phase === 'fadeIn' || phase === 'fadeOut') {
    return {
      animate: { opacity: 1, y: 0 },
      transition: { duration: FADE_S, ease: 'easeOut' },
    };
  }

  if (isBursting(phase)) {
    return {
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.3, ease: 'easeOut', delay: 0.12 },
    };
  }

  return { animate: { opacity: 0, y: 10 }, transition: INSTANT };
};

/** Each effect element's target in a phase (the board's renderVals); the wash is superlikeWashFrame. */
export const superlikeFxFrame = (
  phase: SuperlikeAnimationPhase,
  geometry: SuperlikeFxGeometry,
): SuperlikeFxFrame => {
  const isReduced = phase === 'fadeIn' || phase === 'fadeOut';

  return {
    heart: heartFrame(phase, geometry),
    rings: isReduced ? [] : [ringFrame(phase, 0), ringFrame(phase, 1)],
    particles: isReduced ? [] : particleFrames(phase),
    label: labelFrame(phase),
  };
};

/**
 * What the cards do: the sent card flies up (or fades out under reduced
 * motion); the card under it waits at .96 and settles to 1 as it leaves.
 */
export type SuperlikeCardMotion = 'flyUp' | 'fadeOut' | 'under' | 'settle';

export const superlikeCardMotionTarget = (
  motion: SuperlikeCardMotion,
): TargetAndTransition => {
  switch (motion) {
    case 'flyUp':
      return {
        y: '-118%',
        rotate: -4,
        transition: { duration: 0.43, ease: CARD_FLY_EASE },
      };
    case 'fadeOut':
      return { opacity: 0, transition: { duration: FADE_S, ease: 'easeOut' } };
    case 'under':
      return { scale: 0.96, transition: INSTANT };
    case 'settle':
      return { scale: 1, transition: { duration: 0.43, ease: 'easeOut' } };
  }
};

/** The sent card's and the next card's motion in a phase, if any. */
export const superlikeCardMotions = (
  phase: SuperlikeAnimationPhase,
): { sent?: SuperlikeCardMotion; next?: SuperlikeCardMotion } => {
  switch (phase) {
    case 'launch0':
    case 'launch':
    case 'burst0':
    case 'burst':
      return { next: 'under' };
    case 'fly':
      return { sent: 'flyUp', next: 'settle' };
    case 'fadeOut':
      return { sent: 'fadeOut' };
    default:
      return {};
  }
};
